import React, { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import {
  getCreditCards,
  getPanCardCustomer,
  addBasicCustomer,
  applyCreditCard,
} from "../../Services/creditCard";
import debounce from "lodash.debounce";

function ApplyCreditCard() {
  const [creditCards, setCreditCards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCard, setSelectedCard] = useState(null);
  const [formData, setFormData] = useState({
    panCardNo: "",
    firstName: "",
    lastName: "",
    mobile: "",
    email: "",
    pincode: "",
    createdBy: "",
  });
  const [suggestions, setSuggestions] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    const fetchCreditCards = async () => {
      try {
        const data = await getCreditCards();
        setCreditCards(data);
      } catch (error) {
        console.error("Error fetching credit cards:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchCreditCards();
  }, []);

  // Debounced fetch for PAN card input
  const fetchSuggestions = async (panCardNo) => {
    const user = JSON.parse(localStorage.getItem("user"));
    
    if (!user || !user.id) {
      console.error("User ID is missing. Please log in.");
      setSuggestions([]);
      setShowDropdown(false);
      return;
    }

    const createdBy = user.id;

    if (panCardNo.length === 10) {
      try {
        const customer = await getPanCardCustomer(panCardNo, createdBy);

        if (customer && Object.keys(customer).length > 0) {
          setSuggestions([customer]);
          setShowDropdown(true);
        } else {
          setSuggestions([]);
          setShowDropdown(false);
        }
      } catch (error) {
        console.error("Error fetching customer details:", error);
        setSuggestions([]);
        setShowDropdown(false);
      }
    } else {
      setSuggestions([]);
      setShowDropdown(false);
    }
  };

  const debouncedFetch = debounce(fetchSuggestions, 500);

  useEffect(() => {
    debouncedFetch(formData.panCardNo);
  }, [formData.panCardNo]);

  const handleInputChange = (e) => {
    const { name: inputName, value } = e.target;
    setFormData({
      ...formData,
      [inputName]: inputName === "panCardNo" ? value.toUpperCase() : value,
    });
  };

  const handleSelectPan = (customer) => {
    setFormData({
      ...formData,
      panCardNo: customer.panCardNo,
      firstName: customer.firstName,
      lastName: customer.lastName,
      mobile: customer.mobile,
      email: customer.email,
      pincode: customer.pincode,
    });

    setSuggestions([]);
    setShowDropdown(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedCard) return;
  
    const user = JSON.parse(localStorage.getItem("user"));
    if (!user || !user.id) {
      console.error("User ID is missing. Please log in.");
      return;
    }
  
    let customerId = null; // Store customer ID
  
    try {
      // Check if customer exists based on PAN number
      let customer;
      try {
        customer = await getPanCardCustomer(formData.panCardNo, user.id);
        if (customer && customer.id) {
          customerId = customer.id;
          console.log("Existing customer found with ID:", customerId);
        }
      } catch (error) {
        console.warn("Customer not found, creating a new one.");
      }
  
      // If customer does not exist, add a new one
      if (!customerId) {
        const newCustomer = await addBasicCustomer({
          ...formData,
          createdBy: user.id,
        });
  
        if (newCustomer) {
          customerId = newCustomer;
          console.log("New customer added successfully with ID:", customerId);
        } else {
          console.error("Failed to get customer ID from response.");
          return;
        }
      }
  
      // Validate selected credit card
      const availableCreditCard = creditCards.find((card) => card.id === selectedCard.id);
      if (!availableCreditCard) {
        console.error("Selected credit card is not available.");
        return;
      }
  
      // Apply for credit card with obtained customer ID
      const applicationData = {
        creditCardId: availableCreditCard.id,
        customerId: customerId,
        createdBy: user.id,
      };
  
      const response = await applyCreditCard(applicationData);
      console.log("Credit Card Application Submitted:", response);
  
      // Hide modal
      const modalElement = document.getElementById("creditcard-modal");
      const modalInstance = window.bootstrap.Modal.getInstance(modalElement);
      if (modalInstance) {
        modalInstance.hide();
      }
  
      // Cleanup overlay
      setTimeout(() => {
        document.querySelectorAll(".modal-backdrop").forEach((backdrop) => {
          backdrop.remove();
        });
        document.body.classList.remove("modal-open");
      }, 500);
  
      // Open credit card application link
      window.open(availableCreditCard.urllinkcreditcard, "_blank");
  
      // Reset form
      setFormData({
        panCardNo: "",
        firstName: "",
        lastName: "",
        mobile: "",
        email: "",
        pincode: "",
        createdBy: "",
      });
  
    } catch (error) {
      console.error("Failed to apply for credit card:", error);
    }
  };
  

  const handleOpenModal = (card) => {
    setFormData({
      panCardNo: "",
      firstName: "",
      lastName: "",
      mobile: "",
      email: "",
      pincode: "",
    });
    setSelectedCard(card);

    setTimeout(() => {
      const modalElement = document.getElementById("creditcard-modal");
      if (modalElement) {
        const modalInstance = new window.bootstrap.Modal(modalElement);
        modalInstance.show();
      }
    }, 100);
  };

  return (
    <div>
      <div className="content-page">
        <div className="content">
          <div className="container-fluid">
            <div className="row">
              <div className="col-12">
                <div className="page-title-box">
                  <div className="page-title-right">
                    <ol className="breadcrumb m-0">
                      <li className="breadcrumb-item">
                        <Link to="/dashboard">Dashboard</Link>
                      </li>
                      <li className="breadcrumb-item active">All Credit Cards</li>
                    </ol>
                  </div>
                  <h4 className="page-title">Apply Credit Card</h4>
                </div>
              </div>
            </div>

            <div className="row">
              {loading ? (
                <p>Loading credit cards...</p>
              ) : creditCards.length > 0 ? (
                creditCards.map((card) => (
                  <div className="col-xl-4 col-lg-12" key={card.id}>
                    <div className="card">
                      <div className="card-body">
                        <span className="float-start m-2 me-4">
                          <img
                            src={`https://api.mysfs24.com/${card.imagecreditcard.replace('../', '')}`}
                            style={{ height: 100 }}
                            alt={card.banknamecreditcard}
                            className="img-thumbnail"
                          />
                        </span>
                        <div>
                          <h4 className="mt-1 mb-1">{card.banknamecreditcard.toUpperCase()}</h4><br/>
                          <ul className="mb-0 list-inline">
                            <li className="list-inline-item me-3">
                              <p className="mb-0 font-13">Remarks:</p>
                              {card.remarks.length < 5 ? (
                                  "--"
                                ) : (
                              <h5 className="mb-1">{card.remarks}</h5>
                                  
                                )}
                            </li><br/>
                            <li className="list-inline-item me-3">
                              <p className="mb-0 font-13">Pincode List:</p>
                              <h5 className="mb-1">
                                {card.pincodeList.length < 5 ? (
                                  "--"
                                ) : (
                                  <a href={card.pincodeList} target="_blank" rel="noopener noreferrer">
                                    Pincode List Link
                                  </a>
                                )}
                              </h5>
                            </li>
                          </ul>
                          <br />
                          <div className="d-grid">
                          
                            <button
                              type="button"
                              key={card.id}
                              className="btn btn-sm btn-primary"
                              onClick={() => handleOpenModal(card)}
                            >
                              Apply Now
                            </button>
                              
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ))
              ) : (
                <p>No credit cards available.</p>
              )}

              {/* Modal for Applying Credit Card */}
              <div id="creditcard-modal" className="modal fade" tabIndex={-1} role="dialog" aria-hidden="true">
                <div className="modal-dialog">
                  <div className="modal-content">
                    <div className="modal-body">
                      <div className="text-center mt-2 mb-4">
                        {selectedCard && (
                          <>
                            <img src={selectedCard.imagecreditcard} alt="" height={50} />
                            <h5 className="mt-2">{selectedCard.banknamecreditcard.toUpperCase()}</h5>
                          </>
                        )}
                      </div>
                      <form className="ps-3 pe-3" onSubmit={handleSubmit}>
                        
                        {/* PAN Form Fields */}
                        <div className="mb-3">
                          <label htmlFor="panCardNo" className="form-label">
                            PAN Card Number
                          </label>
                          <input
                            className="form-control"
                            type="text"
                            id="panCardNo"
                            name="panCardNo"
                            value={formData.panCardNo}
                            onChange={handleInputChange}
                            required
                            maxLength={10}
                            placeholder="Enter your PAN Card No"
                          />
                          {showDropdown && suggestions.length > 0 && (
                            <ul ref={dropdownRef} className="absolute w-full bg-white border rounded mt-1 max-h-40 overflow-y-auto shadow-lg">
                              {suggestions.map((customer, index) => (
                                <li
                                  key={index}
                                  onClick={() => handleSelectPan(customer)}
                                  className="p-2 cursor-pointer hover:bg-gray-200"
                                >
                                  {customer.firstName} {customer.lastName}
                                </li>
                              ))}
                            </ul>
                          )}
                        </div>

                        <div className="mb-3">
                          <label htmlFor="firstName" className="form-label">
                            First Name
                          </label>
                          <input
                            className="form-control"
                            type="text"
                            id="firstName"
                            name="firstName"
                            value={formData.firstName}
                            onChange={handleInputChange}
                            required
                            placeholder="Enter first name"
                          />
                        </div>
                        <div className="mb-3">
                          <label htmlFor="lastName" className="form-label">
                            Last Name
                          </label>
                          <input
                            className="form-control"
                            type="text"
                            id="lastName"
                            name="lastName"
                            value={formData.lastName}
                            onChange={handleInputChange}
                            required
                            placeholder="Enter last name"
                          />
                        </div>

                        <div className="mb-3">
                          <label htmlFor="mobile" className="form-label">
                            Mobile Number
                          </label>
                          <input
                            className="form-control"
                            type="text"
                            id="mobile"
                            name="mobile"
                            value={formData.mobile}
                            onChange={handleInputChange}
                            required
                            placeholder="Enter mobile number"
                          />
                        </div>

                        <div className="mb-3">
                          <label htmlFor="mobile" className="form-label">
                            Email
                          </label>
                          <input
                            className="form-control"
                            type="text"
                            id="email"
                            name="email"
                            value={formData.email}
                            onChange={handleInputChange}
                            required
                            placeholder="Enter Email Address"
                          />
                        </div>

                        <div className="mb-3">
                          <label htmlFor="pincode" className="form-label">
                            Pincode
                          </label>
                          <input
                            className="form-control"
                            type="text"
                            id="pincode"
                            name="pincode"
                            value={formData.pincode}
                            onChange={handleInputChange}
                            required
                            placeholder="Enter pincode"
                          />
                        </div>
                        <div className="d-grid">
                          <button type="submit" className="btn btn-primary">Submit</button>
                        </div>
                      </form>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ApplyCreditCard;
